import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import ActionButton from "../../formInputs/buttons/ActionButton";
import DifferentiationHierarchyPanel from "./PanelUI/DifferentiationHierarchyPanel";
import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import TabButton from "../../formInputs/buttons/TabButton";

import { getGradingCriteriaId } from "../../../utils/teacher/InputRatingUtils";
import { GlobalContext } from "../../contextProvider/ContextProvider";
import { gradeTranslation, subjectIdToContentAreaId } from "../../../utils/SchoolAdmin/translations";
import { idFor, makeKeyFor } from "./PanelUI/differentiationHierarchyPanelUtils";
import { useReportableHierarchy } from "../../../utils/TemplateToReportableHierarchy";

import "../../../styles/StudentDiffForm.scss";

const StudentDiffForm = ({
    existingRatingsData,
    onApplyExclusions,
    onSaved,
    setShow,
    student,
    selectedTemplate,
    studentProfileData
}) => {
    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, token, userDetails } = globalState || {};

    const [gradingProfile, setGradingProfile] = useState(null);
    const [studentDiffKey, setStudentDiffKey] = useState(null);
    const [excludedItems, setExcludedItems] = useState([]);
    const [active, setActive] = useState("");
    const [allContentAreas, setAllContentAreas] = useState([]);
    const [reportableContent, setReportableContent] = useState(null);
    const [diffItems, setDiffItems] = useState([]);
    const [studentDiffOwnerGuid, setStudentDiffOwnerGuid] = useState(null);
    const [viewMode, setViewMode] = useState("CURRENT");

    const isCurrent = viewMode === "CURRENT";
    const isDiff = viewMode === "DIFF";

    const { tree: currentTree } = useReportableHierarchy(selectedTemplate);

    const displayToTag = Object.fromEntries(Object.entries(gradeTranslation).map(([tag, display]) => [display, tag]));
    const allGrades = ["K", "FIRST", "SECOND", "THIRD", "FOURTH", "FIFTH", "SIXTH", "SEVENTH", "EIGHTH"];
    function buildEligibleGradeTags(gradingProfile) {
        const isSecondSemester = currentTimePeriodDto.name === "2nd Semester";

        const currentTag = displayToTag[gradingProfile?.gradeName];
        const startIdx = currentTag ? allGrades.indexOf(currentTag) : -1;

        if (startIdx === -1) return allGrades;

        // When it's 2nd semester, skip the current grade (start from the next one)
        const adjustedStart = isSecondSemester ? startIdx + 1 : startIdx;

        return allGrades.slice(adjustedStart);
    }
    const eligible = buildEligibleGradeTags(gradingProfile);

    const makeKey = (n) => `${n.contentType}:${n.contentId}`;

    const handleAddToggle = (node) => {
        const key = makeKey(node);
        setDiffItems((prev) => {
            const exists = prev?.some((p) => makeKey(p) === key);
            return exists
                ? prev?.filter((p) => makeKey(p) !== key)
                : [
                      ...prev,
                      {
                          ...node,
                          grade: node.grade || active,
                          personalizedLearningPlan: node.personalizedLearningPlan ?? false
                      }
                  ];
        });
    };

    const handleExcludeToggle = (node) => {
        const key = makeKeyFor(node, "CURRENT");
        setExcludedItems((prev) => {
            const exists = prev?.some((p) => makeKeyFor(p, "CURRENT") === key);
            return exists ? prev?.filter((p) => makeKeyFor(p, "CURRENT") !== key) : [...prev, node];
        });
    };

    const handleSaveDiff = () => {
        const gradingCriteria = getGradingCriteriaId(student, null, gradingProfile, selectedTemplate, "HOMEROOM");

        const diffRatings = diffItems.map((dI) => {
            return {
                key: dI.key ?? null,
                grade: dI.grade || active || null,
                gradeToContentId: dI.gradeToContentId,
                contentId: dI.contentId,
                contentType: dI.contentType,
                rating: dI.rating ?? "UNRATED",
                comment: dI.comment ?? null,
                personalizedLearningPlan: dI.personalizedLearningPlan ?? false,
                lastRaterGuid: userDetails.uid
            };
        });
        const payload = {
            key: studentDiffKey ?? null,
            gradingCriteria: Number(gradingCriteria),
            gradingProfileStudentId: student.gradingProfileStudentId,
            ownerGuid: studentDiffOwnerGuid ?? userDetails.uid,
            diffRatings
        };
        const options = {
            action: studentDiffKey ? "updateStudentDiff" : "createStudentDiff",
            data: payload,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const rows = response.data.payload?.diffRatings || [];
                    // student_diff responses often omit grade (esp. 7th/8th).
                    // Re-stamp from the request / active tab before onSaved.
                    const byContentId = new Map(diffRatings.map((d) => [String(d.contentId), d]));
                    const withGrades = rows.map((r) => ({
                        ...r,
                        grade: r.grade || byContentId.get(String(r.contentId))?.grade || active || null
                    }));
                    toast.success("Differentiated content items saved successfully.");
                    setShow(false);
                    setDiffItems([]);
                    try {
                        onSaved?.(withGrades);
                    } catch (err) {
                        console.error(err);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("Differentiated content items were not saved. Please try again.");
            });
    };

    // Call the parent with the exact templateContentIds to exclude, then reset UI
    const handleSaveExcluded = async () => {
        // `contentId` here is the templateContentId in your rating table vocabulary
        const toExclude = excludedItems.map((n) => ({
            templateContentId: idFor(n, "CURRENT"),
            contentType: n.contentType
        }));
        const result = await onApplyExclusions?.(toExclude);
        setExcludedItems([]);
        if (result === "SWITCH_TO_DIFF") {
            setViewMode("DIFF");
        }
    };

    useEffect(() => {
        if (!student || !selectedTemplate || !gradingProfile) return;
        const gradingCriteriaId = getGradingCriteriaId(student, null, gradingProfile, selectedTemplate, "HOMEROOM");
        const options = {
            action: "getStudentDiff",
            gradingProfileStudentId: student.gradingProfileStudentId,
            params: {
                gradingCriteriaId
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    if (response.data.payload.length > 0) {
                        setDiffItems(response.data.payload[0]?.diffRatings);
                        setStudentDiffKey(response.data.payload[0]?.key);
                        setStudentDiffOwnerGuid(response.data.payload[0]?.ownerGuid);
                    }
                }
            })
            .catch(console.error);
    }, [gradingProfile, selectedTemplate, student, token]);

    useEffect(() => {
        if (!token) return;
        if (!gradingProfile && studentProfileData?.gradingProfileId) {
            const options = {
                action: "getGradingProfileById",
                gradingProfileId: studentProfileData.gradingProfileId,
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    const dto = response?.data?.payload;
                    setGradingProfile(dto);
                })
                .catch(console.error);
        }
    }, [gradingProfile, studentProfileData, token]);

    useEffect(() => {
        if (!token) return;
        if (allContentAreas.length === 0) {
            const options = {
                action: "allContentAreasRead",
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        setAllContentAreas(payload);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [allContentAreas, setAllContentAreas, token]);

    /**
     * If there is a selected content area fetch reportable content for all grades
     */
    useEffect(() => {
        if (!token) return;
        if (selectedTemplate && !reportableContent && eligible) {
            const options = {
                action: "eprReportableByContentAreaAndGrades",
                contentAreaKey: subjectIdToContentAreaId[selectedTemplate.subjectId],
                gradeLevels: eligible.join(", "),
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        setReportableContent(payload);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [allContentAreas, eligible, reportableContent, selectedTemplate, setReportableContent, token]);

    useEffect(() => {
        if (viewMode === "DIFF" && eligible?.length > 0 && !active) {
            setActive(eligible[0]);
        }
    }, [viewMode, eligible, active]);

    useEffect(() => {
        if (!existingRatingsData?.[0]?.contentRatings) return;

        // Filter out ratings that are "EXCLUDED" or "EXCLUDES"
        const excluded = existingRatingsData[0].contentRatings.filter((r) => r.rating === "EXCLUDED");

        // Map into the same node-like structure used elsewhere in your component
        const initialExcludedItems = excluded.map((r) => ({
            templateContentId: r.templateContentId,
            contentType: r.contentType,
            contentId: r.templateContentId, // keep both for safety
            rating: r.rating
        }));

        setExcludedItems(initialExcludedItems);
    }, [existingRatingsData]);

    const activeReportableContent = reportableContent?.gradeLevels?.filter((grade) => grade.gradeLevel === active) || [
        {}
    ];

    const { tree: diffTree } = useReportableHierarchy(activeReportableContent[0], "CONTENT");
    const selectedKeys = useMemo(() => new Set(diffItems?.map(makeKey)), [diffItems]);
    const selectedKeysCurrent = useMemo(
        () => new Set(excludedItems?.map((n) => makeKeyFor(n, "CURRENT"))),
        [excludedItems]
    );
    return (
        <main className="diff-form">
            {/* Full-width toggle switch */}
            <header className="diff-form__toggle">
                <label className="view-switch" aria-label="Toggle view">
                    <input
                        type="checkbox"
                        checked={isDiff}
                        onChange={(e) => setViewMode(e.target.checked ? "DIFF" : "CURRENT")}
                        aria-checked={isDiff}
                    />
                    <span className="view-switch__track">
                        <span className={`view-switch__option ${isCurrent ? "is-active" : ""}`}>Template Content</span>
                        <span className={`view-switch__option ${isDiff ? "is-active" : ""}`}>
                            Differentiated Content
                        </span>
                        <span className="view-switch__thumb" />
                    </span>
                </label>
            </header>

            {/* Full-height body */}
            <div className="diff-form__body">
                {isCurrent && (
                    <section className="current-section">
                        <div className="section-helper">
                            Below you will see all content items included for the student from the school template. You
                            may elect to exclude all or some of the content items for the student if no rating/comment
                            will be entered due to differentiation. Note that any content items that you elect to
                            exclude will appear at the bottom of the list.
                            <br /> <br /> To add differentiated content for the student, click on the Differentiated
                            Content tab.
                        </div>
                        <div className="current-content-items">
                            <DifferentiationHierarchyPanel
                                dataType="CURRENT"
                                selectedTemplate={selectedTemplate}
                                tree={currentTree}
                                selectedKeys={selectedKeysCurrent}
                                selectedCount={excludedItems?.length}
                                onToggle={handleExcludeToggle}
                            />
                        </div>
                    </section>
                )}

                {isDiff && (
                    <section className="add-panel">
                        <div className="section-helper">
                            Once you have selected a grade level, you will be able to select content items to add for
                            the student. Please note that during Semester 1, it is possible to add additional content
                            items from the student&apos;s current grade if needed based on student&apos;s accelerated
                            instruction/assessment.
                        </div>
                        <div className="add-panel__toolbar">
                            <h5 className="add-panel__title">Search Content to Add</h5>
                        </div>
                        <div className="add-panel__content">
                            <div className="add-panel__tabs add-panel__tabs--inset">
                                <div className="w-100 mb-1">
                                    <strong>Select a Grade Level:</strong>
                                </div>
                                {eligible.map((gradeTag) => {
                                    const isSelected = active === gradeTag;
                                    return (
                                        <div key={gradeTag} className="define-btn-wrapper">
                                            <TabButton
                                                className={isSelected ? "tab-button-selected" : "tab-button"}
                                                disable={!eligible.includes(gradeTag)}
                                                onClick={() => setActive(gradeTag)}
                                                size="small"
                                            >
                                                <div>
                                                    <p className="primary m-0">{gradeTranslation[gradeTag]}</p>
                                                </div>
                                            </TabButton>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Results below tabs */}
                            {activeReportableContent && activeReportableContent.length > 0 && (
                                <DifferentiationHierarchyPanel
                                    dataType="DIFF"
                                    selectedTemplate={selectedTemplate}
                                    tree={diffTree}
                                    counts={{}}
                                    selectedKeys={selectedKeys}
                                    selectedCount={diffItems?.length}
                                    onToggle={handleAddToggle}
                                />
                            )}
                        </div>
                    </section>
                )}
            </div>
            {active !== "" && isDiff && (
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                    <ActionButton label="Save" onClick={() => handleSaveDiff()} />
                </div>
            )}
            {isCurrent && (
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                    <ActionButton label="Save Exclusions" onClick={handleSaveExcluded} />
                </div>
            )}
        </main>
    );
};

StudentDiffForm.propTypes = {
    existingRatingsData: PropTypes.instanceOf(Array),
    onApplyExclusions: PropTypes.func,
    onSaved: PropTypes.func,
    selectedTemplate: PropTypes.object,
    setShow: PropTypes.func,
    student: PropTypes.object,
    studentProfileData: PropTypes.object
};
StudentDiffForm.defaultProps = {
    onApplyExclusions: () => {},
    onSaved: () => {},
    selectedTemplate: null,
    setShow: () => {},
    student: null,
    studentProfileData: null
};

export default StudentDiffForm;
